import { SUPPORTED_LOCALES } from "@repo/shared";
import type {
  LocaleCode,
  PublicLinkContactInfoResponse,
  PublicLinkUnavailableReason,
} from "@repo/shared/types";
import { apiErrorBody, apiErrorCode, get } from "../_client";

/**
 * QL-505 — who the client/group link (`/p/:token/auth`) signs in and how. When
 * the client is represented (or a group's representative member) ONLY that
 * representative may sign in — `data.representativeName` is set and the
 * one-time code goes to their own masked contact details.
 *
 * On a withdrawn link the API answers 410/404 with a `PublicLinkUnavailableResponse`
 * body (`{ error, code: "link_unavailable", reason }`) instead of `data` — use
 * `unavailableReasonFromError` to read it out of the thrown `ApiError`.
 */
export function getPublicLinkContactInfo(publicToken: string) {
  return get<PublicLinkContactInfoResponse>(`/public/flow/${publicToken}/contact-info`);
}

/**
 * Reads `PublicLinkUnavailableReason` off a failed `getPublicLinkContactInfo`
 * call. Null for anything else (a network error, an unrelated 4xx/5xx) — the
 * caller falls back to a generic message rather than mis-attributing it.
 */
export function unavailableReasonFromError(err: unknown): PublicLinkUnavailableReason | null {
  if (apiErrorCode(err) !== "link_unavailable") return null;
  const reason = apiErrorBody(err)?.reason;
  return typeof reason === "string" ? (reason as PublicLinkUnavailableReason) : null;
}

/**
 * QL-17 issue 027 — the language a `/public` error body is written in (the
 * flow's, when the link named one), so a pre-session page can render its own
 * text around the message in the same language. Null for a network failure
 * or any value that is not a supported locale.
 */
export function languageFromError(err: unknown): LocaleCode | null {
  return supportedLocaleOrNull(apiErrorBody(err)?.language);
}

/** `value` when it is a supported locale code, else null (e.g. an untrusted `?lang=`). */
export function supportedLocaleOrNull(value: unknown): LocaleCode | null {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value)
    ? (value as LocaleCode)
    : null;
}
